import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Calendar, Users, TrendingUp, Trash2, Edit, Plus, Download } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useToast } from '../hooks/use-toast';
import { CourseFormDialog } from '../components/CourseFormDialog';
import { CourseDateFormDialog } from '../components/CourseDateFormDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminPanel = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [courseDates, setCourseDates] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [courseDialogOpen, setCourseDialogOpen] = useState(false);
  const [courseDateDialogOpen, setCourseDateDialogOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedCourseDate, setSelectedCourseDate] = useState(null);
  
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [selectedDateForParticipants, setSelectedDateForParticipants] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);

  useEffect(() => {
    const isAuthenticated = sessionStorage.getItem('admin_authenticated');
    if (!isAuthenticated) {
      navigate('/admin');
    }
  }, [navigate]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [datesRes, coursesRes, bookingsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/course-dates`),
        axios.get(`${BACKEND_URL}/courses`),
        axios.get(`${BACKEND_URL}/admin/bookings`)
      ]);

      const now = new Date();
      const upcomingDates = datesRes.data.filter(date => {
        try {
          return new Date(date.date) >= now;
        } catch {
          return false;
        }
      });

      const upcomingDateIds = upcomingDates.map(d => d.id);
      const upcomingBookings = bookingsRes.data.filter(b => upcomingDateIds.includes(b.course_date_id));

      setStats({
        total_bookings: bookingsRes.data.length,
        active_courses: coursesRes.data.length,
        upcoming_courses: upcomingDates.length,
        total_participants: upcomingBookings.length
      });
      setBookings(bookingsRes.data);
      setCourseDates(datesRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast({ title: "Fehler", description: "Daten konnten nicht geladen werden.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const fetchParticipants = async (dateId) => {
    try {
      setLoadingParticipants(true);
      const response = await axios.get(`${BACKEND_URL}/admin/course-dates/${dateId}/participants`);
      setParticipants(response.data.participants);
      setSelectedDateForParticipants(response.data.course_date);
      setShowParticipantsModal(true);
    } catch (error) {
      toast({ title: "Fehler", description: "Teilnehmer konnten nicht geladen werden.", variant: "destructive" });
    } finally {
      setLoadingParticipants(false);
    }
  };

  const exportParticipantsExcel = async (dateId) => {
    try {
      toast({ title: "Export wird erstellt...", description: "Bitte warten." });
      const response = await axios.get(`${BACKEND_URL}/admin/course-dates/${dateId}/participants/excel`, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'Teilnehmerliste.xlsx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({ title: "Export erfolgreich!", description: "Die Teilnehmerliste wurde heruntergeladen." });
    } catch (error) {
      toast({ title: "Export Fehler", description: "Teilnehmerliste konnte nicht exportiert werden.", variant: "destructive" });
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Buchung wirklich löschen?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/admin/bookings/${bookingId}`);
      toast({ title: "Gelöscht", description: "Buchung wurde gelöscht." });
      fetchDashboardData();
    } catch (error) {
      toast({ title: "Fehler", description: "Buchung konnte nicht gelöscht werden.", variant: "destructive" });
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Kurs wirklich löschen?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/admin/courses/${courseId}`);
      toast({ title: "Gelöscht", description: "Kurs wurde gelöscht." });
      fetchDashboardData();
    } catch (error) {
      toast({ title: "Fehler", description: "Kurs konnte nicht gelöscht werden.", variant: "destructive" });
    }
  };

  const handleDeleteCourseDate = async (dateId) => {
    if (!window.confirm('Termin wirklich löschen?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/admin/course-dates/${dateId}`);
      toast({ title: "Gelöscht", description: "Termin wurde gelöscht." });
      fetchDashboardData();
    } catch (error) {
      toast({ title: "Fehler", description: "Termin konnte nicht gelöscht werden.", variant: "destructive" });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button onClick={() => navigate('/')} variant="outline" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />Zurück
              </Button>
              <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
            </div>
            <Button onClick={() => { sessionStorage.removeItem('admin_authenticated'); navigate('/admin'); }} variant="outline" size="sm">Logout</Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Gesamt Buchungen</CardTitle>
              <Users className="h-4 w-4 text-[#D4AF37]" />
            </CardHeader>
            <CardContent><div className="text-2xl font-bold">{stats?.total_bookings || 0}</div></CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Anstehende Kurse</CardTitle>
              <Calendar className="h-4 w-4 text-[#D4AF37]" />
            </CardHeader>
            <CardContent><div className="text-2xl font-bold">{stats?.upcoming_courses || 0}</div></CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Teilnehmer (anstehend)</CardTitle>
              <TrendingUp className="h-4 w-4 text-[#D4AF37]" />
            </CardHeader>
            <CardContent><div className="text-2xl font-bold">{stats?.total_participants || 0}</div></CardContent>
          </Card>
        </div>

        <Tabs defaultValue="bookings" className="space-y-4">
          <TabsList>
            <TabsTrigger value="bookings">Buchungen</TabsTrigger>
            <TabsTrigger value="courses">Kurse</TabsTrigger>
            <TabsTrigger value="dates">Termine</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings">
            <Card>
              <CardHeader><CardTitle>Alle Buchungen</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {bookings.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">Keine Buchungen vorhanden.</div>
                  ) : (
                    bookings.map((booking) => (
                      <div key={booking.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                        <div className="flex-1">
                          <div className="font-semibold">{booking.first_name} {booking.last_name}</div>
                          <div className="text-sm text-gray-600">{booking.email}</div>
                          <div className="text-sm text-gray-600 mt-1">{booking.course_title}</div>
                          <div className="text-xs text-gray-500 mt-1">Gebucht: {format(new Date(booking.booking_date), 'dd.MM.yyyy HH:mm', { locale: de })}</div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge variant="default">{booking.status}</Badge>
                          <Button variant="ghost" size="sm" onClick={() => deleteBooking(booking.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="courses">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Alle Kurse</CardTitle>
                <Button onClick={() => { setSelectedCourse(null); setCourseDialogOpen(true); }} className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] hover:from-[#B8960C] hover:to-[#D4AF37] text-white">
                  <Plus className="w-4 h-4 mr-2" />Neuer Kurs
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {courses.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">Keine Kurse vorhanden.</div>
                  ) : (
                    courses.map((course) => (
                      <div key={course.id} className="border rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-lg">{course.title}</h3>
                              <Badge variant="outline">{course.level || course.target_group}</Badge>
                            </div>
                            <p className="text-sm text-gray-600 mb-3">{course.description}</p>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              <span className="font-semibold text-[#D4AF37]">€{course.price?.toFixed(2) || '0.00'}</span>
                              <span>{course.duration}</span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button onClick={() => { setSelectedCourse(course); setCourseDialogOpen(true); }} variant="outline" size="sm"><Edit className="w-4 h-4" /></Button>
                            <Button onClick={() => handleDeleteCourse(course.id)} variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="dates">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Alle Termine</CardTitle>
                <Button onClick={() => { setSelectedCourseDate(null); setCourseDateDialogOpen(true); }} className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] hover:from-[#B8960C] hover:to-[#D4AF37] text-white">
                  <Plus className="w-4 h-4 mr-2" />Neuer Termin
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {courseDates.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">Keine Termine vorhanden.</div>
                  ) : (
                    courseDates.map((date) => (
                      <div key={date.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                        <div className="flex-1">
                          <div className="font-semibold">{date.course_title}</div>
                          <div className="text-sm text-gray-600">{format(new Date(date.date), 'EEEE, dd. MMMM yyyy', { locale: de })} - {date.time} Uhr</div>
                          <div className="text-sm text-gray-600 mt-1">{date.location}</div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <Button onClick={() => fetchParticipants(date.id)} variant="outline" size="sm" className="text-[#D4AF37] hover:bg-[#D4AF37]/10">
                            <Users className="w-4 h-4 mr-1" />Teilnehmer ({date.current_participants || 0})
                          </Button>
                          <div className="text-right">
                            <div className="text-sm font-semibold">{date.current_participants || 0} / {date.max_participants || 20}</div>
                            <div className="text-xs text-gray-600">Teilnehmer</div>
                          </div>
                          {date.is_full && <Badge variant="destructive">Ausgebucht</Badge>}
                          <div className="flex gap-2">
                            <Button onClick={() => { setSelectedCourseDate(date); setCourseDateDialogOpen(true); }} variant="outline" size="sm"><Edit className="w-4 h-4" /></Button>
                            <Button onClick={() => handleDeleteCourseDate(date.id)} variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <CourseFormDialog open={courseDialogOpen} onClose={() => { setCourseDialogOpen(false); setSelectedCourse(null); }} course={selectedCourse} onSuccess={fetchDashboardData} />
      <CourseDateFormDialog open={courseDateDialogOpen} onClose={() => { setCourseDateDialogOpen(false); setSelectedCourseDate(null); }} courseDate={selectedCourseDate} courses={courses} onSuccess={fetchDashboardData} />

      {showParticipantsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">Teilnehmerliste</h2>
                  {selectedDateForParticipants && (
                    <p className="text-sm text-gray-600 mt-1">{selectedDateForParticipants.course_title} - {format(new Date(selectedDateForParticipants.date), 'EEEE, dd. MMMM yyyy', { locale: de })} - {selectedDateForParticipants.time} Uhr</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {selectedDateForParticipants && participants.length > 0 && (
                    <Button onClick={() => exportParticipantsExcel(selectedDateForParticipants.id)} className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] hover:from-[#B8960C] hover:to-[#D4AF37] text-white">
                      <Download className="w-4 h-4 mr-2" />Excel Export
                    </Button>
                  )}
                  <Button onClick={() => { setShowParticipantsModal(false); setSelectedDateForParticipants(null); setParticipants([]); }} variant="outline">Schließen</Button>
                </div>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {loadingParticipants ? (
                <div className="flex items-center justify-center py-8"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div></div>
              ) : participants.length === 0 ? (
                <div className="text-center py-8 text-gray-500">Noch keine Anmeldungen für diesen Termin</div>
              ) : (
                <div className="space-y-3">
                  {participants.map((participant, index) => (
                    <div key={participant.id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#D4AF37] text-white flex items-center justify-center font-semibold">{index + 1}</div>
                          <div>
                            <div className="font-semibold">{participant.first_name} {participant.last_name}</div>
                            <div className="text-sm text-gray-600">{participant.email}</div>
                            {participant.phone && <div className="text-sm text-gray-600">{participant.phone}</div>}
                            {(participant.street || participant.city) && (
                              <div className="text-sm text-gray-600">
                                {[participant.street, participant.house_number].filter(Boolean).join(' ')}{participant.street && ', '}{[participant.zip_code, participant.city].filter(Boolean).join(' ')}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-500">Gebucht am</div>
                          <div className="text-sm">{format(new Date(participant.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}</div>
                        </div>
                      </div>
                      {participant.referred_by && (
                        <div className="mt-2 pt-2 border-t text-sm text-gray-600">
                          Empfohlen von: <span className="font-semibold">{participant.referred_by}</span>
                        </div>
                      )}
                    </div>
                  ))}
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-[#D4AF37]">{participants.length}</div>
                    <div className="text-sm text-gray-600">Angemeldete Teilnehmer</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
